/**
 * DecisionActionBar - Sticky bottom bar with Approve/Reject buttons + confirmation modal
 */

import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { lendingReportAPI } from '../../../../services/lending-report-api.service';
import type { DecisionActionBarProps, LendingDecisionType } from '../../../../types/lending-report.types';

export const DecisionActionBar: React.FC<DecisionActionBarProps> = ({
  consentId,
  existingDecision,
  onDecisionSubmitted,
}) => {
  const [confirming, setConfirming] = useState<LendingDecisionType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal on Escape key
  useEffect(() => {
    if (!confirming) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) setConfirming(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [confirming, submitting]);

  // Focus trap — focus modal when it opens
  useEffect(() => {
    if (confirming && modalRef.current) {
      modalRef.current.focus();
    }
  }, [confirming]);

  // If decision already exists, don't show action bar
  if (existingDecision) {
    return null;
  }

  const handleConfirm = async () => {
    if (!confirming) return;

    try {
      setSubmitting(true);
      setError(null);
      const response = await lendingReportAPI.submitDecision(consentId, confirming);
      onDecisionSubmitted(response.data);
      setConfirming(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit decision';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Sticky Action Bar */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderTop: '2px solid #e2e8f0',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {error && (
          <div style={{ marginBottom: '12px', padding: '8px 12px', fontSize: '13px', color: '#dc2626', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => setConfirming('APPROVED')}
            aria-label="Approve lending application"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 28px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: '#16a34a',
              border: '2px solid #15803d',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#15803d'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#16a34a'; }}
          >
            <CheckCircle style={{ width: 18, height: 18 }} />
            Approve
          </button>
          <button
            onClick={() => setConfirming('REJECTED')}
            aria-label="Reject lending application"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 28px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: '#dc2626',
              border: '2px solid #b91c1c',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b91c1c'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; }}
          >
            <XCircle style={{ width: 18, height: 18 }} />
            Reject
          </button>
          </div>
          <p style={{ fontSize: '12px', color: '#92400e', backgroundColor: '#fffbeb', padding: '6px 12px', borderRadius: '6px', border: '1px solid #fde68a', margin: 0 }}>
            This action is irreversible. Once submitted, the decision cannot be changed.
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirming && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={() => !submitting && setConfirming(null)}>
          <div ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={`Confirm ${confirming === 'APPROVED' ? 'Approval' : 'Rejection'}`} style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxWidth: '400px', width: '100%', margin: '0 16px', outline: 'none' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              Confirm {confirming === 'APPROVED' ? 'Approval' : 'Rejection'}
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: 1.5 }}>
              Are you sure you want to {confirming === 'APPROVED' ? 'approve' : 'reject'} this
              lending application? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setConfirming(null)}
                disabled={submitting}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#374151',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                style={{
                  padding: '8px 20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#ffffff',
                  backgroundColor: confirming === 'APPROVED' ? '#16a34a' : '#dc2626',
                  border: `1px solid ${confirming === 'APPROVED' ? '#15803d' : '#b91c1c'}`,
                  borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Submitting...' : confirming === 'APPROVED' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
